import React from 'react'
import { formatCurrency, entryPriceForTeam, interpolate } from 'utils'

/**
 * Mobile-only price summary for the team hero.
 *
 * The sidebar price card lives in a `hidden lg:block` container, so below `lg`
 * there is no amount anywhere until the courses section — 1,937px down on Peak
 * Academy. This answers "roughly what does this team cost" before the visitor
 * decides whether to keep scrolling, and stays out of the way on desktop where
 * the full package ladder is already on screen.
 *
 * Each figure is a control: tapping one starts the same flow the matching row
 * in the sidebar price card starts, on the same course and the same package.
 * A free trial claims rather than charges, mirroring TeamPriceCard.
 */
export default function HeroPriceAnchor({ courses, translations, onPurchase, onClaimTrial, trialUsed, hasBalance }) {
  // Both figures describe one course. Taking the cheapest trial and the
  // cheapest lesson from different courses would advertise an offer that no
  // course sells.
  const { startingPrice, trialPrice, currency, course, startingPackage, trialPackage, groupSize } = entryPriceForTeam(courses)
  // A student who already used their trial is offered it nowhere else on this
  // page, so the anchor hides it too rather than opening a claim the backend
  // will refuse.
  const hasTrial = typeof trialPrice === 'number' && !trialUsed
  const hasStarting = typeof startingPrice === 'number'
  const interactive = !!(onPurchase || onClaimTrial)

  // A student already holding lessons is past the "what does this cost"
  // question, which is the only question this answers. MobileBookingBar swaps
  // its buttons for the same state.
  if (hasBalance) return null

  // A team selling only a trial is exactly the team that most needs a figure
  // in the hero, so one figure is enough to render.
  if (!hasStarting && !hasTrial) return null

  const code = currency || 'TWD'

  const buyTrial = () => {
    if (!course || !trialPackage) return
    if (trialPrice === 0) {
      onClaimTrial?.(course)
    } else {
      onPurchase?.(course, trialPackage)
    }
  }

  const buyStarting = () => {
    if (!course || !startingPackage) return
    onPurchase?.(course, startingPackage)
  }

  // A zero trial is a real free trial; a zero lesson price is a teacher who has
  // not set one, and printing "free" there would turn a data gap into an offer.
  const figure = (label, amount, onActivate, freeWhenZero) => {
    const body = (
      <>
        <span className="block text-xs" style={{ color: '#717171' }}>{label}</span>
        <span className="block text-base font-bold" style={{ color: '#222222' }}>
          {amount === 0 && freeWhenZero ? (translations?.free || formatCurrency(0, code)) : formatCurrency(amount, code)}
        </span>
      </>
    )

    if (!interactive) return <div>{body}</div>

    return (
      <button
        type="button"
        onClick={onActivate}
        // 44px minimum touch target: this sits in the first screen on a phone,
        // where the whole point is that it can be acted on.
        className="text-left min-h-[44px] cursor-pointer rounded-lg px-1 -mx-1 hover:opacity-80 transition-opacity"
      >
        {body}
      </button>
    )
  }

  return (
    // inline-flex, so a course with no trial gets a box the width of its one
    // figure instead of a full-width frame around a single number.
    <div className="lg:hidden mt-4 inline-flex items-center gap-6 rounded-xl border border-gray-200 px-4 py-3">
      {hasTrial && figure(translations?.trialLabel, trialPrice, buyTrial, true)}
      {hasStarting && figure(
        groupSize
          ? interpolate(translations?.groupStartingLabel || 'For %{count} people, per person from', { count: groupSize })
          : translations?.startingLabel,
        startingPrice, buyStarting, false
      )}
    </div>
  )
}
